import { useCallback, useEffect, useState } from 'react'

import { compressImage, HeicConversionError, isHeicFile } from '@modules/utils/imageCompression'

const HEIC_CONVERSION_ERROR_MESSAGE = '画像の変換に失敗しました。別の写真でお試しください'
const IMAGE_PROCESSING_ERROR_MESSAGE =
  '画像の処理に失敗しました。もう一度お試しいただくか、別の写真をお選びください'

interface UseImageUploadProps {
  resetSelections: () => void
  /**
   * true の場合、HEIC変換・画像圧縮 (compressImage) を一切行わず選択ファイルをそのまま使う。
   * 画像以外のファイル (PDF 等) を同じアップロードフローに乗せたいホスト向けの拡張点
   * (issue #2489 積算トップの PDF アップロードで使用)。既定 false は AI カラーの挙動を変えない。
   */
  skipCompression?: boolean
}

interface ImageDimensions {
  width: number
  height: number
}

export const useImageUpload = (props: UseImageUploadProps) => {
  const { resetSelections, skipCompression = false } = props
  const [imagePreviews, setImagePreviews] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [originalDimensions, setOriginalDimensions] = useState<ImageDimensions | null>(null)
  const [hasUploadedButNotProcessed, setHasUploadedButNotProcessed] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // プレビュー用 Object URL のメモリリーク防止:
  // imagePreviews が切り替わったとき、およびアンマウント時に旧 URL を解放する
  useEffect(() => {
    return () => {
      if (imagePreviews) {
        URL.revokeObjectURL(imagePreviews)
      }
    }
  }, [imagePreviews])

  // uploadFile が外部から null クリアされた（アップロード成功・ダイアログを閉じた等）際、
  // 古いプレビュー URL を保持し続けないよう imagePreviews を同期クリアする
  useEffect(() => {
    if (!uploadFile) {
      setImagePreviews(null)
    }
  }, [uploadFile])

  const logImageMetrics = useCallback(async (label: string, file: File) => {
    const url = URL.createObjectURL(file)
    try {
      const metrics = await new Promise<ImageDimensions>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve({ width: img.width, height: img.height })
        img.onerror = () => reject(new Error('Failed to load image for metrics'))
        img.src = url
      })
      console.info(`[ai-color][upload] ${label}`, {
        name: file.name,
        type: file.type,
        sizeBytes: file.size,
        width: metrics.width,
        height: metrics.height,
      })
    } catch (error) {
      console.warn(`[ai-color][upload] ${label} metrics failed`, error)
    } finally {
      URL.revokeObjectURL(url)
    }
  }, [])

  /**
   * 画像ファイルアップロード処理
   * 画像を圧縮し、HEICファイルの場合はJPEGに変換する
   * @param e - ファイル入力イベント
   */
  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      resetSelections()
      const file = e.target.files?.[0]
      if (!file) return

      setError(null)
      setIsProcessing(true)
      try {
        // 計測ログは診断目的であり、アップロード本流をブロックしない（fire-and-forget）
        void logImageMetrics('input_original', file)

        // HEIC/HEIF形式の場合はJPEGに変換し、画像を圧縮する。
        // 非HEICの圧縮失敗は表示可能な元画像なので原本にフォールバックして継続する。
        // HEICは変換できないと表示・AI処理ができないため、失敗時は明示エラーで中断する。
        // skipCompression=true (画像以外のファイルを扱うホスト向け) では
        // 圧縮ライブラリが画像デコードを前提にしているため一切呼ばず原本をそのまま使う。
        let fileToUse: File
        if (skipCompression) {
          fileToUse = file
        } else {
          try {
            fileToUse = await compressImage(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 2048,
              quality: 1,
              useWebWorker: true,
            })
          } catch (compressErr) {
            if (compressErr instanceof HeicConversionError || isHeicFile(file)) {
              throw compressErr
            }
            console.warn('画像圧縮に失敗したため元ファイルを使用します:', compressErr)
            fileToUse = file
          }
        }

        void logImageMetrics('input_compressed', fileToUse)
        setUploadFile(fileToUse)

        // 旧 URL の解放は imagePreviews 変化を監視する useEffect が担う
        const previewUrl = URL.createObjectURL(fileToUse)
        setImagePreviews(previewUrl)

        // 寸法計測用の画像。読み込み失敗してもアップロード自体は妨げない
        // （無限ローディングは isProcessing の finally 解除で防止済み。
        //  ここで error を立てると、有効な uploadFile があってもボタンが無効化されてしまう）
        // skipCompression では画像以外もあり得るため、寸法計測（Image デコード）自体を行わない。
        if (!skipCompression) {
          const previewImg = new Image()
          previewImg.onload = () => {
            setOriginalDimensions({
              width: previewImg.width,
              height: previewImg.height,
            })
          }
          previewImg.src = previewUrl
        }

        setHasUploadedButNotProcessed(true)
      } catch (err) {
        // 失敗を握りつぶさず、ユーザーへ明示エラーを表示する（無限ローディング防止）
        console.error('画像処理エラー:', err)
        setError(
          err instanceof HeicConversionError || isHeicFile(file)
            ? HEIC_CONVERSION_ERROR_MESSAGE
            : IMAGE_PROCESSING_ERROR_MESSAGE,
        )
      } finally {
        setIsProcessing(false)
        e.target.value = ''
      }
    },
    [logImageMetrics, resetSelections, skipCompression],
  )

  return {
    handleImageUpload,
    imagePreviews,
    uploadFile,
    setUploadFile,
    originalDimensions,
    setOriginalDimensions,
    hasUploadedButNotProcessed,
    setHasUploadedButNotProcessed,
    isProcessing,
    error,
    setError,
  }
}
